import { CoralogixRum } from "@coralogix/browser";

class CoralogixLogger {
  private static instance: CoralogixLogger;
  private initialized = false;
  private currentUserContext: any = null;

  private constructor() {
    this.initializeCoralogix();
  }

  static getInstance(): CoralogixLogger {
    if (!CoralogixLogger.instance) {
      CoralogixLogger.instance = new CoralogixLogger();
    }
    return CoralogixLogger.instance;
  }

  private initializeCoralogix(): void {
    try {
      const config = this.getConfig();

      if (!config.apiKey || !config.domain) {
        console.warn(
          "Coralogix configuration missing - logging will be disabled"
        );
        return;
      }

      CoralogixRum.init({
        public_key: config.apiKey,
        application: "marketing-frontend",
        version: config.version,
        coralogixDomain: config.domain,
        environment: config.environment,
        debug: config.environment === "development",
        instrumentations: {
          errors: true,
          fetch: true,
          xhr: true,
          custom: true,
          long_tasks: true,
          resources: true,
          interactions: true,
          web_vitals: true,
        },
      });

      this.initialized = true;
    } catch (error) {
      console.error("Failed to initialize Coralogix:", error);
    }
  }

  private getConfig() {
    // eslint-disable-next-line no-underscore-dangle
    const env = (window as any).__env__ || {};

    return {
      apiKey: env.CORALOGIX_API_KEY || "",
      domain: env.CORALOGIX_DOMAIN || "US1",
      environment: env.BASE_DOMAIN?.includes("stage")
        ? "stage"
        : env.BASE_DOMAIN?.includes("prod")
        ? "production"
        : "development",
      buildNumber: "unknown",
      commitSha: "unknown",
      version: "unknown",
    };
  }

  // Legacy method for backward compatibility - now does nothing
  initialize(): void {
    console.warn(
      "Coralogix.initialize() is deprecated - initialization now happens automatically"
    );
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  log(
    message: string,
    data?: unknown,
    level: "debug" | "info" | "warn" | "error" = "info"
  ): void {
    if (!this.initialized) {
      console.warn(
        "Coralogix not initialized, logging to console:",
        message,
        data
      );
      return;
    }

    try {
      const logData = {
        message,
        timestamp: new Date().toISOString(),
        level,
        application: "marketing-frontend",
        ...(data && typeof data === "object" ? data : { data }),
      };

      switch (level) {
        case "debug":
          CoralogixRum.debug(message, logData);
          break;
        case "info":
          CoralogixRum.info(message, logData);
          break;
        case "warn":
          CoralogixRum.warn(message, logData);
          break;
        case "error":
          CoralogixRum.error(message, logData);
          break;
      }
    } catch (error) {
      console.error("Failed to log to Coralogix:", error);
      console.log("Original log data:", message, data);
    }
  }

  logError(
    message: string,
    error: unknown,
    context: Record<string, any> = {}
  ): void {
    if (!this.initialized) {
      console.error("Coralogix not initialized:", message, error, context);
      return;
    }

    try {
      const errorData = {
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
        ...context,
        // Explicitly include user context in each log
        user: this.currentUserContext,
        userId: this.currentUserContext?.userId,
        userEmail: this.currentUserContext?.userEmail,
        firstName: this.currentUserContext?.firstName,
        lastName: this.currentUserContext?.lastName,
        timestamp: new Date().toISOString(),
        source: "marketing-frontend",
      };

      // Log the error with context including user info (using instant-quote-app method name)
      CoralogixRum.error(message, errorData);

      // If it's an Error object, also capture the exception with user context
      if (error instanceof Error) {
        const exceptionPayload = {
          ...context,
          ...this.currentUserContext,
          errorMessage: message,
        };

        CoralogixRum.captureError(error, exceptionPayload);
      }
    } catch (coralogixError) {
      console.error("Failed to log to Coralogix:", coralogixError);
      console.error("Original error:", message, error, context);
    }
  }

  logInfo(message: string, context: Record<string, any> = {}): void {
    if (!this.initialized) {
      console.info("Coralogix not initialized:", message, context);
      return;
    }

    try {
      CoralogixRum.info(message, {
        ...context,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Failed to log info to Coralogix:", error);
    }
  }

  logWarning(message: string, context: Record<string, any> = {}): void {
    if (!this.initialized) {
      console.warn("Coralogix not initialized:", message, context);
      return;
    }

    try {
      CoralogixRum.warn(message, {
        ...context,
        timestamp: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Failed to log warning to Coralogix:", error);
    }
  }

  // Legacy method for backward compatibility
  error(message: string, error?: Error | unknown, context?: unknown): void {
    this.logError(message, error, context as Record<string, any>);
  }

  addUserContext(userInfo: Record<string, any>): void {
    if (!this.initialized) {
      console.warn("Coralogix not initialized, cannot set user context");
      return;
    }

    try {
      const userId = userInfo.id || userInfo.user_id || "";
      const userEmail =
        userInfo.email || userInfo.user_email || userInfo.emailAddress || "";
      const firstName = userInfo.firstName || "";
      const lastName = userInfo.lastName || "";
      const userName =
        firstName && lastName
          ? `${firstName} ${lastName}`
          : firstName || lastName || "";

      const userContext = {
        userId,
        userEmail,
        firstName,
        lastName,
        userName,
        userMetadata: userInfo,
      };

      // Store user context locally to include in each log
      this.currentUserContext = userContext;

      // Set user context in Coralogix RUM SDK
      CoralogixRum.setUserContext({
        user_id: userId,
        user_name: userName,
        user_email: userEmail,
        user_metadata: {
          firstName,
          lastName,
          ...userInfo,
        },
      });

      console.log("Coralogix user context set successfully");
    } catch (error) {
      console.error("Failed to set user context in Coralogix:", error);
    }
  }

  // Legacy method for backward compatibility
  setUserContext(user: {
    id?: string;
    email?: string;
    [key: string]: unknown;
  }): void {
    this.addUserContext(user);
  }

  addCustomAttribute(key: string, value: any): void {
    if (!this.initialized) {
      return;
    }

    try {
      const labels = { [key]: value };
      CoralogixRum.setLabels(labels);
    } catch (error) {
      console.error("Failed to add custom attribute to Coralogix:", error);
    }
  }

  // Legacy method for backward compatibility
  addLabel(key: string, value: string): void {
    this.addCustomAttribute(key, value);
  }
}

export default CoralogixLogger.getInstance();
